import { Box, Flex, Text, VStack } from '@minetrace/ui'
import { LuShieldCheck } from 'react-icons/lu'
import { formatDateTime } from '../../lib/dates'
import type { CertificadoPublicoResponse } from './verificacionPublica'

/**
 * El certificado de aprobación de un minero, identificado por su CUID. Es el OTRO
 * código que puede llegar a `/verificar/:code`; el índice digital (CVD) lo resuelve
 * `IndiceResultado`.
 *
 * Extraído tal cual de la página, sin cambios de comportamiento: sólo deja de vivir
 * dentro del componente de ruta para que ésta pueda elegir entre los dos resultados.
 */
export function CertificadoResultado({ data }: { data: CertificadoPublicoResponse }) {
  return (
    <Box bg="white" borderRadius="xl" shadow="0 8px 32px rgba(0,0,0,0.06)" overflow="hidden">
      {/* Status header */}
      <Flex bg="#eef7f6" px={6} py={4} alignItems="center" gap={3} borderBottom="1px solid #bfe3df">
        <LuShieldCheck size={24} color="#1d6664" />
        <Box>
          <Text fontSize="lg" fontWeight="700" color="#1d6664">Certificado Valido</Text>
          <Text fontSize="xs" color="#414844">Este minero ha sido verificado y aprobado por el sistema KUNTUR</Text>
        </Box>
      </Flex>

      {/* Data */}
      <Box px={6} py={6}>
        <VStack gap={5} alignItems="stretch">
          <Flex gap={8} flexWrap="wrap">
            <Box flex={1} minW="200px">
              <Text fontSize="xs" color="#857b71" textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">Codigo CUID</Text>
              <Text fontSize="lg" fontWeight="700" fontFamily="monospace" color="#012d1d" mt={1}>{data.cuidCode}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="#857b71" textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">Estado</Text>
              <Box mt={1} px={3} py={1} borderRadius="full" bg="#dcefed" color="#1d6664" fontSize="sm" fontWeight="700" display="inline-block">
                {data.registrationStatus}
              </Box>
            </Box>
          </Flex>

          <Box h="1px" bg="#e0e3e1" />

          <Flex gap={8} flexWrap="wrap">
            <Box flex={1} minW="200px">
              <Text fontSize="xs" color="#857b71" textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">RUC</Text>
              <Text fontSize="md" fontWeight="600" fontFamily="monospace" color="#2a2622" mt={1}>{data.ruc}</Text>
            </Box>
            <Box flex={2} minW="200px">
              <Text fontSize="xs" color="#857b71" textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">Razon Social</Text>
              <Text fontSize="md" fontWeight="600" color="#2a2622" mt={1}>{data.legalName}</Text>
            </Box>
          </Flex>

          {data.commercialName && (
            <Box>
              <Text fontSize="xs" color="#857b71" textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">Nombre Comercial</Text>
              <Text fontSize="md" color="#2a2622" mt={1}>{data.commercialName}</Text>
            </Box>
          )}

          <Flex gap={8} flexWrap="wrap">
            <Box flex={1}>
              <Text fontSize="xs" color="#857b71" textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">Fecha de Autorizacion</Text>
              <Text fontSize="md" color="#2a2622" mt={1}>{formatDateTime(data.authorizedAt)}</Text>
            </Box>
            {data.minerSource && (
              <Box flex={1}>
                <Text fontSize="xs" color="#857b71" textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">Fuente</Text>
                <Text fontSize="md" color="#2a2622" mt={1}>{data.minerSource === 'FormalizedMiner' ? 'Minero Formalizado' : 'REINFO'}</Text>
              </Box>
            )}
            {data.reinfoStatus && (
              <Box flex={1}>
                <Text fontSize="xs" color="#857b71" textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">Estado REINFO</Text>
                <Text fontSize="md" color="#2a2622" mt={1}>{data.reinfoStatus}</Text>
              </Box>
            )}
          </Flex>
        </VStack>
      </Box>

      {/* Footer */}
      <Box px={6} py={4} bg="#f1f4f2" borderTop="1px solid #e0e3e1">
        <Text fontSize="xs" color="#857b71" textAlign="center">
          Este certificado fue generado por KUNTUR - Compliance & Debida Diligencia. La informacion mostrada es de caracter referencial.
        </Text>
      </Box>
    </Box>
  )
}
